import { useEffect, useMemo, useState } from 'react';
import { Search, User, X } from 'lucide-react';

export type ChartPersonOption = {
  id: string;
  name: string | null;
  email: string;
  role: string | null;
  custom_data: unknown;
  channels: unknown;
};

export default function ChartPersonPickerModal({
  isOpen,
  onClose,
  people,
  selectedId,
  onSelect,
}: {
  isOpen: boolean;
  onClose: () => void;
  people: ChartPersonOption[];
  selectedId: string | null;
  onSelect: (person: ChartPersonOption | null) => void;
}) {
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = [...people].sort((a, b) => {
      const an = (a.name || a.email).toLowerCase();
      const bn = (b.name || b.email).toLowerCase();
      return an.localeCompare(bn);
    });
    if (!q) return list.slice(0, 80);
    return list
      .filter(
        (p) =>
          (p.name ?? '').toLowerCase().includes(q) ||
          p.email.toLowerCase().includes(q),
      )
      .slice(0, 80);
  }, [people, query]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40">
      <div className="w-full max-w-lg max-h-[85vh] flex flex-col rounded-xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Customize charts</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Pick someone to highlight where they sit vs the group.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              autoFocus
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or email…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
            />
          </div>
          {selectedId ? (
            <button
              type="button"
              onClick={() => {
                onSelect(null);
                onClose();
              }}
              className="mt-2 text-xs text-amber-700 dark:text-amber-300 hover:underline"
            >
              Clear personalization
            </button>
          ) : null}
        </div>

        <ul className="flex-1 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
          {filtered.length === 0 ? (
            <li className="px-5 py-8 text-center text-sm text-gray-500">No matches.</li>
          ) : (
            filtered.map((p) => {
              const active = p.id === selectedId;
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onSelect(p);
                      onClose();
                    }}
                    className={`w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                      active ? 'bg-emerald-50 dark:bg-emerald-900/20' : ''
                    }`}
                  >
                    <span
                      className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                        active
                          ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-500'
                      }`}
                    >
                      <User className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-gray-900 dark:text-white truncate">
                        {p.name || 'No name'}
                      </span>
                      <span className="block text-xs text-gray-500 truncate">{p.email}</span>
                    </span>
                    {active ? (
                      <span className="text-xs text-emerald-600 dark:text-emerald-400 shrink-0">Selected</span>
                    ) : null}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </div>
  );
}
